import React, { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";

type Page = {
  name: string;
  url: string;
  keywords?: string[];
};

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [filteredPages, setFilteredPages] = useState<Page[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false); // State to toggle search box
  const searchRef = useRef<HTMLDivElement>(null); // Ref for the search container

  const pages = useMemo(() => [
    { name: "Retreats", url: "/retreats", keywords: ["Retreat Promotional Video", "La Manga Retreat", "Gut and Brain Retreat", "May 2025 Retreat", "September 2025 Retreat", "Taking Deposits", "Retreat Booking", "Join Our Newsletter", "Subscribe for Updates"] },
    { name: "Nutrition", url: "/nutrition", keywords: ["Nutrition", "Health Support", "Proactive Health", "Functional Tests", "Family Nutrition", "Sports Nutrition", "Fitness Nutrition", "Common Health Issues", "Anxiety", "Depression", "Cardiac Health", "Heart Health", "Cancer Support", "Diabetes Support", "Neurological Disorders", "Parkinson's", "Epilepsy", "Brain Tumours", "Alzheimer's", "Dementia", "Multiple Sclerosis", "Liver Detox", "Heavy Metal Detox", "Mould Cleanse", "Parasite Cleanse", "ADHD", "Autism", "Neurodevelopmental Disorders", "Performance Nutrition", "Anti-aging", "Anti-cancer Protocols", "Gut Health", "Digestive Health", "Baby Nutrition", "Infant Digestion", "Baby Eczema", "Baby Allergies", "Teenage Hormone Support", "Fertility Support", "Pregnancy Nutrition", "Perimenopause Support", "Menopause Support", "Male Hormone Health"] },
    { name: "Treatments", url: "/treatments", keywords: ["Treatments", "Massage Therapy", "Deep Tissue Massage", "Myofascial Release", "Stretch Therapy", "Remedial Massage", "Full Body Massage", "Tailor Made Massage", "Assessment and Rehabilitation", "Injury Rehab", "Posture Correction", "Kinesio Taping", "Shiatsu Massage", "Pregnancy Massage", "Reflexology", "Indian Head Massage", "Wellness Treatments", "Rehabpro", "Oxygen Room", "Yoga on the Edge", "Stocks House", "Terms and Conditions"] },
    { name: "Shop", url: "/shop", keywords: ["Our Products", "Breathe Tea", "Bentonite Clay", "Chill Tea", "Gut Buster", "Herbal Bombs", "Magic Clay", "Herbal Supplements", "Natural Remedies", "Detox Products", "Wellness Products"] },
    { name: "Work with Me", url: "/workwithme", keywords: ["Work With CF Organic Wellness", "Wellness Planning", "Support Packages", "One to One Consultations", "Follow Up Call", "Discovery Call", "Test Results", "Health Consultations", "Personalized Wellness Plans"] },
    { name: "Blog", url: "/blog", keywords: ["Welcome to the Blog", "Featured Blog", "Bone Broth Benefits", "Anxiety Tips", "Lemon Balm Uses", "Anti-Inflammatory Elixir", "Digestive Health", "Wellness Blog", "Natural Remedies", "Join Our Newsletter", "Subscribe for Blog Updates"] },
  ], []); // Memoize the pages array

  useEffect(() => {
    if (query.trim() === "") {
      setFilteredPages([]);
    } else {
      setFilteredPages(
        pages.filter((page) =>
          page.name.toLowerCase().includes(query.toLowerCase()) ||
          (page.keywords && page.keywords.some((keyword) =>
            keyword.toLowerCase().includes(query.toLowerCase())
          ))
        )
      );
    }
  }, [query, pages]); // Include pages in the dependency array

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle item selection
  const handleItemClick = () => {
    setIsSearchOpen(false);
    setQuery(""); // Optionally clear the query
  };

  return (
    <div className="relative" ref={searchRef}>

    
      {/* Search Icon */}

      {!isSearchOpen && (
        <button
          onClick={() => setIsSearchOpen(true)}
          className="p-2.5 rounded-full text-[var(--text-on-dark)] hover:bg-[var(--accent)]/20 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Open search"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </button>
      )}

      {/* Search Input */}
      {isSearchOpen && (
        <div className="relative w-full z-50">
          <input
            type="text"
            placeholder="Search site..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full min-w-[200px] border border-[var(--border-on-dark)] rounded-md p-2 text-sm bg-[var(--bg-card-dark)] text-[var(--text-on-dark)] placeholder:text-[var(--text-on-dark-faint)] focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)]"
          />
          <ul className="absolute bg-[var(--bg-surface)] border border-[var(--border-on-light)] w-full mt-1 z-10 rounded-md shadow-lg">
            {filteredPages.map((page) => (
              <li
                key={page.url}
                className="p-2 text-[var(--text-on-light)] hover:text-[var(--text-brand)] hover:bg-[var(--bg-surface-dk)] cursor-pointer"
                onClick={handleItemClick}
              >
                <Link href={page.url}>{page.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchBar;